import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import '../../lib/i18n';
import './styles.scss';

// Contexts
import { ContextProvider } from '../../context';
import { useConfig } from '../../hooks';

import { ROOT } from '../../lib/data-service';
import { processDealer } from '../../lib/dealers-api';
import gtmEvent from '../../lib/gtmEvents';
import { storeLocation } from '../../lib/utils';

// Components
import Landing from '../Landing';
import MapAndDealers from '../MapAndDealers';

function sendLocationAnalyticsEvent(location) {
  if (location.type === 'user_search') {
    gtmEvent({
      event: 'view_dealer_list',
      'dealer_list_open_method': 'Manual input'
    });
  }
  else if (location.type === 'user_location') {
    gtmEvent({
      event: 'view_dealer_list',
      'dealer_list_open_method': 'Location'
    });
  }
}

function Root({ slug }) {
  const dealerData = window.dealerData || {};
  const initialDealer = slug && dealerData[slug] ? processDealer(dealerData[slug]) : null;
  const { config } = useConfig();

  // User location is either location from browser or location searched by user
  const [userLocation, setUserLocation] = useState(() => {
    if (config.initialLocation?.type === 'address') {
      return config.initialLocation;
    }
    return null;
  });

  // Location is required and the source point which is used to fetch data from api
  // Source point is either users location, location searched by user or single dealer location
  const [location, setLocation] = useState(() => {
    if (initialDealer) {
      return {
        type: 'single_dealer',
        countryCode: initialDealer.country_code,
        address: config.addressFields.map(field => initialDealer[field]).filter(val => val).join(', '),
        lat: initialDealer.lat,
        lng: initialDealer.long,
      };
    }

    if (config.initialLocation) {
      return config.initialLocation;
    }
    return null;
  });

  let view = 'landing';
  if (config.skipLandingPage || location) {
    view = 'dealers';
  }

  const handleLandingPageLocationChange = useCallback((result) => {
    setLocation(result);
    setUserLocation(result);
    sendLocationAnalyticsEvent(result);
    storeLocation(result);
  }, []);

  const handleLocationChange = useCallback((result) => {
    setLocation(result);
    sendLocationAnalyticsEvent(result);
  }, []);

  const handleUserLocationChange = useCallback((result, action = 'both') => {
    if (action === 'both') {
      setLocation(result);
      setUserLocation(result);
      sendLocationAnalyticsEvent(result);
      storeLocation(result);
    }
    else if (action === 'user') {
      setUserLocation(result);
      storeLocation(result);
    }
  }, []);

  // Send event if opening app via popular locations
  useEffect(() => {
    if (config.mapType === 'city' && view === 'dealers') {
      gtmEvent({
        event: 'view_dealer_list',
        'dealer_list_open_method': 'Popular locations'
      });
    }
  }, []);

  console.log('Dealer app rendered', { location, userLocation, slug, config });

  if (view === 'landing') {
    return (
      <Landing showWorldMap={!config.includeCities} onLocationChange={handleLandingPageLocationChange} />
    );
  }

  return (
    <MapAndDealers
      initialDealer={initialDealer}
      location={location}
      userLocation={userLocation}
      onLocationChange={handleLocationChange}
      onUserLocationChange={handleUserLocationChange}
    />
  );
}

Root.propTypes = {
  slug: PropTypes.string,
};


export default function App() {
  const dealerSlug = window.location.pathname.replace(ROOT, '').split('/')[0];
  return (
    <div className="dealers">
      <ContextProvider>
        <Root slug={dealerSlug} />
      </ContextProvider>
    </div>
  );
}
